/**
 * Pure selection + derivation logic for the Soccer Match Drawer
 * (`SoccerMatchDrawer.tsx`). Kept dependency-free (no React, no network) so
 * every rule below is unit-testable in isolation — mirrors `bulkScope.ts` /
 * `entrySort.ts` in this same directory.
 *
 * Selection model: the drawer's checkable unit is a (matchKey, bucket) CELL —
 * one checkbox per non-empty bucket row inside a match section. The backend's
 * `POST /rules/soccer-matches/prepare` contract, though, takes a FLAT
 * `matchKeys: string[]` + `buckets: string[]` pair (a cross-product, not an
 * arbitrary cell set) — so `buildMatchPrepareRequests` below deliberately
 * issues ONE prepare request PER SELECTED MATCH, scoped to that match's own
 * selected buckets, instead of a single cross-product call. This is what
 * keeps "check bucket X only in match M1" from silently also touching bucket
 * X in a co-selected match M2 that happens to share it — the one shortcut the
 * flat contract would otherwise force. `aggregatePrepareResults` then sums
 * the N per-match responses back into one totals object for the confirm
 * dialog / the arm step, so the caller never has to special-case "N calls".
 */
import type {
  PrepareSoccerMatchesResult,
  SoccerMatchBucketConfigEcho,
  SoccerMatchRow,
  SoccerOpenerBucket
} from '../../api/useSoccerMatches';

// ── Bucket display metadata (FE-only; the backend already orders each
// match's `buckets` array canonically — this is just for the drawer's
// bucket-column quick-select row, which lists every bucket seen across ANY
// match in one fixed, human-friendly order). ─────────────────────────────
export const SOCCER_OPENER_BUCKET_ORDER: SoccerOpenerBucket[] = [
  'moneyline',
  'spread',
  'total',
  'btts_ftts',
  'exact_score',
  'first_half_result',
  'first_half_total',
  'player_props',
  'corners',
  'other'
];

export const SOCCER_OPENER_BUCKET_LABELS: Record<SoccerOpenerBucket, string> = {
  moneyline: 'Moneyline',
  spread: 'Spread',
  total: 'Total',
  btts_ftts: 'BTTS / FTTS',
  exact_score: 'Exact score',
  first_half_result: '1H Result',
  first_half_total: '1H Total',
  player_props: 'Player props',
  corners: 'Corners',
  other: 'Other'
};

export const SOCCER_MATCH_PATH_LABELS: Record<'a' | 'b', string> = {
  a: 'Path A — Polymarket YES + Kalshi NO',
  b: 'Path B — Polymarket NO + Kalshi YES'
};

/** Distinct buckets present across `matches`, in the fixed display order. */
export function bucketsAcrossMatches(
  matches: SoccerMatchRow[]
): SoccerOpenerBucket[] {
  const present = new Set<SoccerOpenerBucket>();
  for (const m of matches) for (const b of m.buckets) present.add(b.bucket);
  return SOCCER_OPENER_BUCKET_ORDER.filter(b => present.has(b));
}

// ── Selection (Set<`${matchKey}::${bucket}`>) ────────────────────────────

export function bucketSelectionKey(matchKey: string, bucket: string): string {
  return `${matchKey}::${bucket}`;
}

export function isBucketSelected(
  selected: ReadonlySet<string>,
  matchKey: string,
  bucket: string
): boolean {
  return selected.has(bucketSelectionKey(matchKey, bucket));
}

/** Toggle a single (match, bucket) cell. */
export function toggleBucketCell(
  selected: ReadonlySet<string>,
  matchKey: string,
  bucket: string
): Set<string> {
  const key = bucketSelectionKey(matchKey, bucket);
  const next = new Set(selected);
  if (next.has(key)) next.delete(key);
  else next.add(key);
  return next;
}

/** Check/uncheck every bucket of ONE match (the match-row checkbox). */
export function setMatchSelection(
  selected: ReadonlySet<string>,
  match: SoccerMatchRow,
  checked: boolean
): Set<string> {
  const next = new Set(selected);
  for (const b of match.buckets) {
    const key = bucketSelectionKey(match.matchKey, b.bucket);
    if (checked) next.add(key);
    else next.delete(key);
  }
  return next;
}

export function isMatchFullySelected(
  selected: ReadonlySet<string>,
  match: SoccerMatchRow
): boolean {
  return (
    match.buckets.length > 0 &&
    match.buckets.every(b => isBucketSelected(selected, match.matchKey, b.bucket))
  );
}

export function isMatchPartiallySelected(
  selected: ReadonlySet<string>,
  match: SoccerMatchRow
): boolean {
  const any = match.buckets.some(b =>
    isBucketSelected(selected, match.matchKey, b.bucket)
  );
  return any && !isMatchFullySelected(selected, match);
}

/** The global "select all matches" checkbox: checks/unchecks every cell. */
export function setAllMatchesSelection(
  matches: SoccerMatchRow[],
  checked: boolean
): Set<string> {
  const next = new Set<string>();
  if (!checked) return next;
  for (const m of matches)
    for (const b of m.buckets) next.add(bucketSelectionKey(m.matchKey, b.bucket));
  return next;
}

export function areAllMatchesSelected(
  selected: ReadonlySet<string>,
  matches: SoccerMatchRow[]
): boolean {
  return matches.length > 0 && matches.every(m => isMatchFullySelected(selected, m));
}

export function isAnyMatchSelected(
  selected: ReadonlySet<string>,
  matches: SoccerMatchRow[]
): boolean {
  return matches.some(m =>
    m.buckets.some(b => isBucketSelected(selected, m.matchKey, b.bucket))
  );
}

/**
 * The per-bucket COLUMN toggle: checks/unchecks one bucket across every
 * match that actually has it. Matches lacking that bucket are untouched
 * (there is no cell to check).
 */
export function toggleBucketColumn(
  selected: ReadonlySet<string>,
  matches: SoccerMatchRow[],
  bucket: SoccerOpenerBucket,
  checked: boolean
): Set<string> {
  const next = new Set(selected);
  for (const m of matches) {
    if (!m.buckets.some(b => b.bucket === bucket)) continue;
    const key = bucketSelectionKey(m.matchKey, bucket);
    if (checked) next.add(key);
    else next.delete(key);
  }
  return next;
}

export function isBucketColumnFullySelected(
  selected: ReadonlySet<string>,
  matches: SoccerMatchRow[],
  bucket: SoccerOpenerBucket
): boolean {
  const withBucket = matches.filter(m => m.buckets.some(b => b.bucket === bucket));
  return (
    withBucket.length > 0 &&
    withBucket.every(m => isBucketSelected(selected, m.matchKey, bucket))
  );
}

// ── Count derivation ──────────────────────────────────────────────────────

export interface SelectionCounts {
  /** Matches with at least one selected bucket cell. */
  matchCount: number;
  /** Total selected (match, bucket) cells. */
  bucketCellCount: number;
  /** Sum of `pairCount` across every selected cell. */
  pairCount: number;
}

export function computeSelectionCounts(
  selected: ReadonlySet<string>,
  matches: SoccerMatchRow[]
): SelectionCounts {
  const matchKeysWithSelection = new Set<string>();
  let bucketCellCount = 0;
  let pairCount = 0;
  for (const m of matches) {
    for (const b of m.buckets) {
      if (isBucketSelected(selected, m.matchKey, b.bucket)) {
        bucketCellCount += 1;
        pairCount += b.pairCount;
        matchKeysWithSelection.add(m.matchKey);
      }
    }
  }
  return {
    matchCount: matchKeysWithSelection.size,
    bucketCellCount,
    pairCount
  };
}

/** `true` when at least one SELECTED (match, bucket) cell already has ≥1
 *  armed pair — gates the "Turn off" button (nothing to disarm otherwise). */
export function hasArmedSelection(
  selected: ReadonlySet<string>,
  matches: SoccerMatchRow[]
): boolean {
  for (const m of matches) {
    for (const b of m.buckets) {
      if (isBucketSelected(selected, m.matchKey, b.bucket) && b.state.armed > 0) {
        return true;
      }
    }
  }
  return false;
}

// ── Per-match prepare requests (the cross-product-avoidance step) ────────

export interface MatchPrepareRequest {
  matchKey: string;
  buckets: string[];
}

/** One request per match that has ≥1 selected bucket, scoped to just that
 *  match's own selected buckets — see the file header for why this is NOT a
 *  single flat `{matchKeys, buckets}` call. */
export function buildMatchPrepareRequests(
  selected: ReadonlySet<string>,
  matches: SoccerMatchRow[]
): MatchPrepareRequest[] {
  const out: MatchPrepareRequest[] = [];
  for (const m of matches) {
    const buckets = m.buckets
      .filter(b => isBucketSelected(selected, m.matchKey, b.bucket))
      .map(b => b.bucket);
    if (buckets.length > 0) out.push({ matchKey: m.matchKey, buckets });
  }
  return out;
}

// ── Bucket / match status derivation (OFF / PARTIAL / READY / ON) ────────

export type BucketStatus =
  | { kind: 'OFF' }
  | { kind: 'PARTIAL'; n: number; m: number }
  | { kind: 'READY' }
  | { kind: 'ON' };

/**
 * Derives a bucket's (or an aggregated match's) status purely from the read
 * model's `state` counters + its `pairCount` — NEVER from local/optimistic
 * intent (the drawer's hard invariant, mirroring `resolveArmSwitchState`).
 * The prepare pipeline is monotone (assigned → enabled → ruled → armed), so:
 *   - `armed === pairCount` (and > 0)  → ON, everything armed.
 *   - `0 < armed < pairCount`          → PARTIAL armed/pairCount.
 *   - `armed === 0 && ruled === pairCount` (and > 0) → READY: prepared
 *     (subgroup assigned, enabled, ruled) but not yet armed.
 *   - `armed === 0` and SOME prep progress (ruled/enabled/assigned > 0)
 *     → PARTIAL ruled/pairCount (prep started, not finished).
 *   - otherwise (nothing prepared, or pairCount is 0)             → OFF.
 */
export function deriveBucketStatus(
  state: { assigned: number; enabled: number; ruled: number; armed: number },
  pairCount: number
): BucketStatus {
  if (pairCount <= 0) return { kind: 'OFF' };
  if (state.armed >= pairCount) return { kind: 'ON' };
  if (state.armed > 0) return { kind: 'PARTIAL', n: state.armed, m: pairCount };
  if (state.ruled >= pairCount) return { kind: 'READY' };
  if (state.ruled > 0 || state.enabled > 0 || state.assigned > 0) {
    return { kind: 'PARTIAL', n: state.ruled, m: pairCount };
  }
  return { kind: 'OFF' };
}

export function formatBucketStatusLabel(status: BucketStatus): string {
  switch (status.kind) {
    case 'OFF':
      return 'OFF';
    case 'ON':
      return 'ON';
    case 'READY':
      return 'READY';
    case 'PARTIAL':
      return `PARTIAL ${status.n}/${status.m}`;
  }
}

/** Sums every bucket's `state`/`pairCount` for a match — feeds
 *  `deriveBucketStatus` to get one status chip for the match header. */
export function aggregateMatchState(match: SoccerMatchRow): {
  state: { assigned: number; enabled: number; ruled: number; armed: number };
  pairCount: number;
} {
  let assigned = 0;
  let enabled = 0;
  let ruled = 0;
  let armed = 0;
  let pairCount = 0;
  for (const b of match.buckets) {
    assigned += b.state.assigned;
    enabled += b.state.enabled;
    ruled += b.state.ruled;
    armed += b.state.armed;
    pairCount += b.pairCount;
  }
  return { state: { assigned, enabled, ruled, armed }, pairCount };
}

export function deriveMatchStatus(match: SoccerMatchRow): BucketStatus {
  const { state, pairCount } = aggregateMatchState(match);
  return deriveBucketStatus(state, pairCount);
}

/**
 * The first match with a bucket that isn't fully armed yet
 * (`state.armed < state.assigned`) — auto-expanded so the operator lands on
 * something actionable rather than a fully-armed match. `null` when every
 * bucket of every match is already fully armed (or there are no matches).
 */
export function findAutoExpandMatchKey(matches: SoccerMatchRow[]): string | null {
  for (const m of matches) {
    if (m.buckets.some(b => b.state.armed < b.state.assigned)) return m.matchKey;
  }
  return null;
}

// ── Config-caption formatter ──────────────────────────────────────────────

/**
 * `[{subgroup, thresholdSum, size}]` → the operator-facing caption shown
 * before any button is clickable. One entry → `thr 0.97 · size 100`; 2+
 * entries join by subgroup name, e.g. `Totals & BTTS 0.97/100 · Resto
 * 0.95/50`. Empty → `—` (should not normally happen — every listed bucket is
 * `configResolved:true`).
 */
export function formatBucketConfigCaption(
  config: SoccerMatchBucketConfigEcho[]
): string {
  if (config.length === 0) return '—';
  if (config.length === 1) {
    const [c] = config;
    return `thr ${c.thresholdSum} · size ${c.size}`;
  }
  return config
    .map(c => `${c.subgroup} ${c.thresholdSum}/${c.size}`)
    .join(' · ');
}

/**
 * `null` when every pair in the bucket arms the normal way. Otherwise names
 * the owner-decided override so the operator sees it BEFORE pressing Turn On
 * — never a silent side-swap. `noOnly === pairCount` reads as "arms NO
 * only"; a mixed bucket (e.g. exact_score with some 0-0 and some no-zero
 * pairs) reads as "N of M arm NO only".
 */
export function formatArmDirectiveCaption(
  counts: { path: number; noOnly: number },
  pairCount: number
): string | null {
  if (counts.noOnly <= 0) return null;
  if (counts.noOnly >= pairCount) return 'arms NO only';
  return `${counts.noOnly} of ${pairCount} arm NO only`;
}

// ── Prepare-result aggregation + rule-id resolution ──────────────────────

/** Sums N `PrepareSoccerMatchesResult`s (one per `MatchPrepareRequest`) into
 *  one totals object — same shape, so the confirm dialog / arm step never
 *  has to special-case "how many calls did this take". */
export function aggregatePrepareResults(
  results: PrepareSoccerMatchesResult[]
): PrepareSoccerMatchesResult {
  const aggregate: PrepareSoccerMatchesResult = {
    dryRun: results.length === 0 ? true : results[0].dryRun,
    subgroups: { created: 0, updated: 0 },
    pairs: { matched: 0, assigned: 0, alreadyAssigned: 0, enabled: 0 },
    skipped: [],
    rules: { created: 0, skipped: 0 },
    ruleIdsByPairId: {},
    armDirectiveByPairId: {}
  };
  for (const r of results) {
    aggregate.subgroups.created += r.subgroups.created;
    aggregate.subgroups.updated += r.subgroups.updated;
    aggregate.pairs.matched += r.pairs.matched;
    aggregate.pairs.assigned += r.pairs.assigned;
    aggregate.pairs.alreadyAssigned += r.pairs.alreadyAssigned;
    aggregate.pairs.enabled += r.pairs.enabled;
    aggregate.skipped.push(...r.skipped);
    aggregate.rules.created += r.rules.created;
    aggregate.rules.skipped += r.rules.skipped;
    Object.assign(aggregate.ruleIdsByPairId, r.ruleIdsByPairId);
    Object.assign(aggregate.armDirectiveByPairId, r.armDirectiveByPairId);
  }
  return aggregate;
}

/** The subset of `EntryRule` this module needs to classify an ALREADY-
 *  EXISTING rule's current arm direction — see `resolveTurnOnRuleIds`'s
 *  fallback branch. */
export interface ExistingRuleMakerState {
  _id: string;
  polyYesMaker?: boolean;
  polyNoMaker?: boolean;
  kalshiYesMaker?: boolean;
  kalshiNoMaker?: boolean;
}

export interface RuleIdResolution {
  /** Deduped rule ids to arm via `TAB_FLAGS[activePath]` — the default. */
  pathRuleIds: string[];
  /** Deduped rule ids that must arm NO-only (both NO makers, both YES makers
   *  explicitly off) regardless of the active Path — `armDirectiveByPairId`
   *  said `'no-only'` for these. See `soccerOpenerArmDirectiveFor`. */
  noOnlyRuleIds: string[];
  /** Pairs that ended up with NO resolvable rule id — never created (a write
   *  was skipped) and not already present in the page's rule cache. Surfaced
   *  so the caller can warn instead of silently under-arming. */
  unresolvedPairIds: string[];
}

/**
 * Resolves the final rule-id set to arm/disarm from a (possibly aggregated)
 * prepare response, PARTITIONED by arm direction: `ruleIdsByPairId` already
 * covers "existing OR just-created" for every SUCCESSFULLY-prepared pair, and
 * `armDirectiveByPairId` says which of those go in `pathRuleIds` vs
 * `noOnlyRuleIds`. Pairs the backend `skipped` (e.g. `active-rule` /
 * `open-order`) are NOT in either map — for those, fall back to the page's
 * own `rulesByPairId` (a pair skipped BECAUSE it already has a rule still
 * needs that rule armed/disarmed).
 *
 * CAPITAL-RISK GUARD: a skipped pair has no FRESH `armDirectiveByPairId`
 * verdict (the backend didn't just prepare it), but its EXISTING rule
 * already carries real maker flags — read those instead of blindly
 * defaulting to `'path'`. Re-selecting an already-armed no-only bucket (e.g.
 * via the match-level "select all" checkbox, which does not exclude
 * already-ON buckets) and pressing Turn On again would otherwise skip that
 * pair in `armDirectiveByPairId`, fall through to a `'path'` default, and
 * arm `polyYesMaker`/`kalshiYesMaker` on TOP of its existing
 * `polyNoMaker`/`kalshiNoMaker` — arming YES on exactly the outcome this
 * feature exists to keep NO-only.
 *
 * Classification requires POSITIVE evidence, never a default: both NO flags
 * true → `noOnlyRuleIds`; one of the two canonical single-path signatures
 * (`TAB_FLAGS` 'a' = `polyYesMaker`+`kalshiNoMaker`, 'b' = `polyNoMaker`+
 * `kalshiYesMaker`) → `pathRuleIds`; anything else — including a rule Turn
 * Off just reset to all-four-false, or any other partial/ambiguous
 * combination — → `unresolvedPairIds`. An unknown direction must never be
 * guessed into `pathRuleIds`: `useTurnOffSoccerMatches` deliberately clears
 * ALL FOUR maker flags (not just the active Path's two) so a disarmed rule
 * always lands in this genuinely-unknown bucket rather than silently
 * carrying a stale single flag that could later flip the wrong way.
 *
 * A pair that is neither prepared nor resolvable via the existing-rules map
 * is reported in `unresolvedPairIds` rather than silently dropped.
 */
export function resolveTurnOnRuleIds(
  prepareResult: Pick<
    PrepareSoccerMatchesResult,
    'ruleIdsByPairId' | 'skipped' | 'armDirectiveByPairId'
  >,
  existingRulesByPairId: ReadonlyMap<string, ExistingRuleMakerState>
): RuleIdResolution {
  const pathRuleIds = new Set<string>();
  const noOnlyRuleIds = new Set<string>();
  const unresolvedPairIds: string[] = [];

  for (const [pairId, ruleId] of Object.entries(prepareResult.ruleIdsByPairId)) {
    if (!ruleId) continue;
    if (prepareResult.armDirectiveByPairId[pairId] === 'no-only') {
      noOnlyRuleIds.add(ruleId);
    } else {
      pathRuleIds.add(ruleId);
    }
  }

  const touchedFromPrepare = new Set(Object.keys(prepareResult.ruleIdsByPairId));
  const seenSkipped = new Set<string>();
  for (const skip of prepareResult.skipped) {
    if (touchedFromPrepare.has(skip.pairId) || seenSkipped.has(skip.pairId)) continue;
    seenSkipped.add(skip.pairId);
    const existing = existingRulesByPairId.get(skip.pairId);
    if (!existing) {
      unresolvedPairIds.push(skip.pairId);
      continue;
    }
    // EXACT 4-flag signature match, not a subset match — each check requires
    // its two defining flags true AND the other two NOT true (`!== true`
    // treats `undefined` the same as `false`, since these fields are
    // optional). A rule with a THIRD flag also true (e.g. an inconsistent
    // state left by a manual Entry-table edit on the same rule, or a
    // partial-failure retry) must NOT slip through a subset check into
    // `pathRuleIds` — `polyYesMaker===true && kalshiNoMaker===true` alone,
    // for instance, is also satisfied by a rule that ADDITIONALLY has
    // `kalshiYesMaker:true`, which is not a real Path A rule at all. Turn Off
    // deliberately clears all four maker flags to a clean baseline (see
    // useTurnOffSoccerMatches), so an all-false — or any other
    // partial/ambiguous/over-set — rule reaching this fallback has a
    // genuinely UNKNOWN direction. Defaulting an unknown rule to
    // `pathRuleIds` would arm YES with no positive evidence that's correct;
    // fail closed instead (`unresolvedPairIds`) rather than guess on a
    // capital-risk field.
    const isCurrentlyNoOnly =
      existing.polyNoMaker === true &&
      existing.kalshiNoMaker === true &&
      existing.polyYesMaker !== true &&
      existing.kalshiYesMaker !== true;
    const isCurrentlyPathA =
      existing.polyYesMaker === true &&
      existing.kalshiNoMaker === true &&
      existing.polyNoMaker !== true &&
      existing.kalshiYesMaker !== true;
    const isCurrentlyPathB =
      existing.polyNoMaker === true &&
      existing.kalshiYesMaker === true &&
      existing.polyYesMaker !== true &&
      existing.kalshiNoMaker !== true;
    if (isCurrentlyNoOnly) {
      noOnlyRuleIds.add(existing._id);
    } else if (isCurrentlyPathA || isCurrentlyPathB) {
      pathRuleIds.add(existing._id);
    } else {
      unresolvedPairIds.push(skip.pairId);
    }
  }

  return {
    pathRuleIds: Array.from(pathRuleIds),
    noOnlyRuleIds: Array.from(noOnlyRuleIds),
    unresolvedPairIds
  };
}
